import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Box, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { Row, AdministrativeLevelLabel, FormControlStyled } from "../styled/SidePanel.style";
import TypeSelect from "./TypeSelect";
import SitesDetailsTile from "./SitesDetailTile";
import { ToggleTitleWrapper, ToggleSwitch } from "../styled/ToggleTile.styled";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import { AdvanceCapacityHeaders, monthOrder, monthsByQuarter } from "../../constants/Data";
import { paginatedApiInvoker } from "../../utils/webMapApiHelper";
import { toast } from "react-toastify";
import { getCapacityGeohashes } from "../../services/api/mapGeohashes.api";
import {
  convertGeohashToPolygon
} from "../../utils/helper";
import { ToolWrapper } from "../styled/PanelTools.styled";
import DialogWrapper from "../DialogWrapper";
import {
  getAdvancedCapacityData,
  getCapacityAdvancedTableFilters,
  getCapacityFilters,
} from "../../services/api/capacityService";
import ScrollableTypeSelect from "./ScrollableTypeSelect";
import CapacityAdvanceFilter from "./CapacityAdvanceFilter";
import { removeDuplicateGeoHashes } from "../../helpers/webMap";

export default function CapacitySidePanel({ isPanelOpen = false }) {
  const [openAdvanceCapacityData, setOpenAdvanceCapacityData] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [advanceCapacityData, setAdvanceCapacityData] = useState([]);
  const [advTableFilters, setAdvTableFilters] = useState({ flag: [], action: [], months: [] });
  const [operatorData, setOperatorData] = useState([]);
  const hasInitializedYear = useRef(false); // Tracks if year options were initialized
  const hasInitializedMonth = useRef(false); // Tracks if month options were initialized

  const {
    setCapacityGeohashes,
    appliedFilters,
    setCapacityChartData,
    setSelectedCapacityGeohashes,
    setCapacityFilters,
    capacityFilters,
    setCapacityFilterData,
    capacityFilterData,
    selectedCapacityAdvanceData,
    setSelectedCapacityAdvanceData,
    setCapacityAdvanceDisable,
    capacityAdvanceDisable,
    capacitySubFilters,
    setCapacitySubFilters,
    onChangeCapacitySubFilters,
    getCommonCapacityFilters,
    downloadData,
    setDownloadData,
    operatorsPermittedCapacity = []
  } = useContext(WebMapContext);

  // load years and  reset years, isFirstTime
  useEffect(() => {
    if (appliedFilters.countryCode && isPanelOpen && !capacityFilters.selectedYear) fetchInitialFilterData("year");
  }, [appliedFilters.countryCode, isPanelOpen]);
  
  // load months
  useEffect(() => {
    if (capacityFilters.selectedYear && isPanelOpen) {
      fetchInitialFilterData("month");
    } else {
      setCapacityFilterData((prev) => ({ ...prev, months: [] }));
    }
  }, [capacityFilters.selectedYear, appliedFilters.year, appliedFilters.quarter, isPanelOpen]);

  // set operators
  useEffect(() => {
    if(isPanelOpen){
      setOperatorData(operatorsPermittedCapacity);
    }
  }, [isPanelOpen, operatorsPermittedCapacity]);

  //load filter data for towers & sectors
  useEffect(() => {
    if (!capacityAdvanceDisable) return;
    if (
      capacityFilters.selectedCapacityLvl === "operator" &&
      capacityFilters.selectedCapacityLvlVal
    ) {
      fetchFilterDataOnFocus("enodbID", true, 1);
    } else if (
      capacityFilters.selectedCapacityLvl === "enodbID" &&
      capacityFilters.selectedCapacityLvlVal
    ) {
      fetchFilterDataOnFocus("secID", true, 1);
    } 
  }, [capacityFilters.selectedCapacityLvlVal,appliedFilters]);

  useEffect(() => {
   if(selectedCapacityAdvanceData.length !== 0 && capacityFilters.selectedCapacitySubLvl){
    loadAdvanceSelectedGeohashes(true)
   }
  }, [capacityFilters.selectedCapacitySubLvl,capacityFilters.selectedFlag,capacityFilters.selectedAction]);

  useEffect(() => { // fetch unique flags, actions, months
    if (capacityFilters.selectedYear && capacityFilters.selectedMonth && capacityFilters.selectedOprtr) {
      loadAdvancedTableFilters();
    }
  }, [appliedFilters.countryCode, capacityFilters.selectedYear, capacityFilters.selectedMonth, capacityFilters.selectedOprtr]);

  useEffect(() => {
    // onChangeYear(); // reset all
    hasInitializedYear.current = false;
    hasInitializedMonth.current = false;
  }, [appliedFilters.year, appliedFilters.quarter]);
  
  const onChangeYear = (val) => {
    setCapacityFilters((prev) => ({
      ...capacityFilters,
      selectedYear: val ? val : null,
      selectedOprtr: null,
      selectedMonth: null,
      selectedTower: null,
      selectedSector: null,
      selectedFlag: [],
      selectedAction: [],
      selectedCapacityLvl: null,
      selectedCapacityLvlVal: null,
      selectedCapacitySubLvl: null
    }));
    resetFilters("year");   
  };

  const onChangeMonth = (val) => {
    setCapacityFilters((prev) => ({
      ...prev,
      selectedOprtr: null,
      selectedMonth: val ? val : null,
      selectedTower: null,
      selectedSector: null,
      selectedFlag: [],
      selectedAction: [],
      selectedCapacityLvl: null,
      selectedCapacityLvlVal: null,
      selectedCapacitySubLvl: null
    }));
    resetFilters("month");
  };

  const onChangeCapacityOperator = (val) => {
    if(capacityFilters.selectedOprtr === val) return;
    setCapacityFilters({
      ...capacityFilters,
      selectedOprtr: capacityFilters.selectedMonth && val ? val : null,
      selectedTower: null,
      selectedSector: null,
      selectedFlag: [],
      selectedAction: [],
      selectedCapacityLvl: capacityFilters.selectedMonth && val ? "operator" : null,
      selectedCapacityLvlVal: capacityFilters.selectedMonth && val ? val : null,
      selectedCapacitySubLvl: null
    });
    resetFilters("operator");
  };

  const onChangeTower = (val) => {
    if (val) {
      setCapacityFilters({
        ...capacityFilters,
        selectedTower: val,
        selectedCapacityLvl: "enodbID",
        selectedCapacityLvlVal: val,
        selectedSector: null,
        selectedFlag: [],
        selectedAction: [],
        selectedCapacitySubLvl: null
      });
    } else {
      setCapacityFilters({
        ...capacityFilters,
        selectedTower: null,
        selectedCapacityLvl: "operator",
        selectedCapacityLvlVal: capacityFilters.selectedOprtr,
        selectedSector: null,
        selectedCapacitySubLvl: null
      });
      setCapacityFilterData((prev) => ({
        ...prev,
        towerScroll: 1,
      }));
    }
    resetFilters("enodbID");
  };

  const onChangeSector = (val) => {
    if (val) {
      setCapacityFilters({
        ...capacityFilters,
        selectedSector: val,
        selectedCapacityLvl: "secID",
        selectedCapacityLvlVal: val,
        selectedFlag: [],
        selectedAction: [],
        selectedCapacitySubLvl: null
      });
    } else {
      setCapacityFilters({
        ...capacityFilters,
        selectedSector: null,
        selectedCapacityLvl: "enodbID",
        selectedCapacityLvlVal: capacityFilters.selectedTower,
        selectedCapacitySubLvl: null
      });
    }
  };

  const onChangeFlag = (event, type) => {
    const typeVal = type;
    const isChecked = event.target.checked;

    let updatedFlag = { ...capacitySubFilters.flags };
    updatedFlag = {
      ...updatedFlag,
      [typeVal]: {
        ...updatedFlag[typeVal],
        check: isChecked,
      },
    };
    setCapacitySubFilters({
      ...capacitySubFilters,
      flags: updatedFlag,
    });
    const selectedFlags = Object.keys(updatedFlag).filter(
      (key) => updatedFlag[key].check === true
    );
    setCapacityFilters({
      ...capacityFilters,
      selectedFlag: selectedFlags,
    });
  };

  const onChangeAction = (event, type) => {
    const typeVal = type;
    const isChecked = event.target.checked;

    let updatedAction = { ...capacitySubFilters.actions };
    updatedAction = {
      ...updatedAction,
      [typeVal]: {
        ...updatedAction[typeVal],
        check: isChecked,
      },
    };
    setCapacitySubFilters({
      ...capacitySubFilters,
      actions: updatedAction,
    });
    const selectedActions = Object.keys(updatedAction).filter(
      (key) => updatedAction[key].check === true
    );
    setCapacityFilters({
      ...capacityFilters,
      selectedAction: selectedActions,
    });
  };

  const resetFilters = (filterLvl) => {
    if (filterLvl === "year") {
      clearAdvanceSelectedData();
      setCapacityChartData({});
      setCapacityFilterData((prev) => ({
        ...prev,
        months: [],
        operators: [],
        towers: [],
        sectors: [],
        towerScroll: 1,
        sectorScroll: 1,
      }));
      setCapacitySubFilters({
        flags: {},
        actions: {},
      });
    } else if (filterLvl === "month") {
      clearAdvanceSelectedData();
      setCapacityChartData({});
      setCapacityFilterData((prev) => ({
        ...prev,
        operators: [],
        towers: [],
        sectors: [],
        towerScroll: 1,
        sectorScroll: 1,
      }));
      setCapacitySubFilters({
        flags: {},
        actions: {},
      });
    } else if (filterLvl === "operator") {
      setCapacityFilterData((prev) => ({
        ...prev,
        towerScroll: 1,
        sectorScroll: 1,
        towers: [],
        sectors: [],
      }));
      clearAdvanceSelectedData();
    } else if (filterLvl === "enodbID") {
      setCapacityFilterData((prev) => ({
        ...prev,
        sectorScroll: 1,
        sectors: [],
      }));
    }
  };

  const resetFiltersInBaseSearch = async () => {
    setCapacityGeohashes([]);
    setCapacityFilters({
      ...capacityFilters,
      selectedTower: null,
      selectedSector: null,
    });
    setCapacityFilterData((prev) => ({
      ...prev,
      towerScroll: 1,
      sectorScroll: 1,
    }));
    setCapacityAdvanceDisable(true);
  };

  const onDisableTowerSector = () => {
    if (capacityFilters.selectedCapacityLvl !== "operator") {
      setCapacityFilters({
        ...capacityFilters,
        selectedTower: null,
        selectedSector: null,
        selectedCapacityLvl: "operator",
        selectedCapacityLvlVal: capacityFilters.selectedOprtr,
        selectedFlag: [],
        selectedAction: []
      });
    }
    resetFilters("enodbID");
  };

  const onDisableSubFilters = () =>{
    setCapacityFilters({
      ...capacityFilters,
      selectedCapacitySubLvl: null
    });
    setCapacitySubFilters({
      ...capacitySubFilters,
      flags: {},
      actions: {},
    })
  }

  const clearAdvanceSelectedData = () => {
    setSelectedCapacityAdvanceData([]);
    localStorage.setItem("selectedCapacityFilters", JSON.stringify([]));
  };

  const updateAdvanceSelectedData = () => {
      const tempSelectedItems = localStorage.getItem("tempSelectedCapacityFilters")
        ? localStorage.getItem("tempSelectedCapacityFilters")
        : [];
      const convertSelectedTempValue = tempSelectedItems.length !== 0 ? JSON.parse(tempSelectedItems) : [];
      let updatedSelectedValue = [...convertSelectedTempValue ]
      localStorage.setItem(
        "selectedCapacityFilters",
        updatedSelectedValue.length ? JSON.stringify(updatedSelectedValue) : ""
      ); 
  };

  const resetAdvanceSelectedData = async() =>{
    await checkAdvanceClearStatus()
    const selectedItems = localStorage.getItem("selectedCapacityFilters")
    ? localStorage.getItem("selectedCapacityFilters")
    : [];
    const convertSelectedValue = selectedItems.length !== 0 ? JSON.parse(selectedItems) : [];
    if(convertSelectedValue.length === 0){
      setSelectedCapacityAdvanceData([]);
      onChangeCapacitySubFilters()
      setCapacityAdvanceDisable(true)
    }else{
      resetFiltersInBaseSearch();
      loadAdvanceSelectedGeohashes();
      onChangeCapacitySubFilters(true);
    }
  }

  const checkAdvanceClearStatus = () => {
    let updatedData = []
      const selectedItems = localStorage.getItem("selectedCapacityFilters")
      ? localStorage.getItem("selectedCapacityFilters")
      : [];
      const convertSelectedValue = selectedItems.length !== 0 ? JSON.parse(selectedItems) : [];
      if(convertSelectedValue.length !== 0){
        updatedData = convertSelectedValue?.map(({ groupList, ...rest }) => ({
          sectors: groupList,
          ...rest,
          }));
      }
      setSelectedCapacityAdvanceData(updatedData)
  }

  // advance search related
  const getSelectedCapacityData = (selectedCapacity) => {
    const updatedData = selectedCapacity?.map(({ groupList, ...rest }) => ({
      sectors: groupList,
      ...rest,
    }));
    setSelectedCapacityAdvanceData(updatedData);
  };

  const loadAdvanceCapacityData = async (initialFecth,page,sortingOrder,filterArray) => {
    let queryParams = {};
    const filters = getCommonCapacityFilters(true,false)
    queryParams.pageNum = page || 1;
    queryParams.sortParams = sortingOrder || [];
    queryParams.advanceSearchKeys = filterArray || [];
    queryParams = {...queryParams, ...filters}
    
    const response = await getAdvancedCapacityData(queryParams);
    const getPrevSelectedItems = localStorage.getItem("selectedCapacityFilters")
      ? localStorage.getItem("selectedCapacityFilters")
      : [];
    const SelectedList =
      getPrevSelectedItems.length !== 0 ? JSON.parse(getPrevSelectedItems) : [];
    const updatedData = response.data.map(
      ({ _id, rsrq, rsrp, sinr, enodbid, sectors, action, flag, lon, lat }) => {
        const groupList = sectors.map(({ secID, rsrq, rsrp, sinr, months, flag, action  }) => {
          const selectedGroup =
            SelectedList.length > 0
              ? SelectedList.find((selected) =>
                  selected.groupList.some(
                    (group) => group.secID === secID && selected.enodbid === enodbid && group.selected
                  )
                )
              : false;
          const selected = selectedGroup ? true : false;
          return {secID,rsrq,rsrp,sinr,flag,action,
            months,
            // ...months.reduce(
            //   (acc, month, index) => ({
            //     ...acc,
            //     [`month${index + 1}`]: month,
            //   }),
            //   {}
            // ),
            selected: selected,
          };
        });
        return {groupList,expand: false,_id,rsrq,rsrp,sinr,enodbid,action, flag, lat, lon};
      }
    );

    if (initialFecth) {
      setAdvanceCapacityData(updatedData);
      setOpenAdvanceCapacityData(true);
    } else {
      return {
        rows: updatedData,
        page: 1, //response?.data?.page,
        pageLimit: 10, //response?.data?.limit,
      };
    }
  };

  const loadAdvanceSelectedGeohashes = async (isSubFilterChange) => {
    let allFilters = {};
    if(isSubFilterChange){
      if ((capacityFilters.selectedCapacitySubLvl === 'flag' && !capacityFilters.selectedFlag.length) ||
      (capacityFilters.selectedCapacitySubLvl === 'action' && !capacityFilters.selectedAction.length)
    ) {
      setSelectedCapacityGeohashes([]);
      return;
    }
      allFilters.flag = capacityFilters.selectedCapacitySubLvl === 'flag' && capacityFilters.selectedFlag.length ? capacityFilters.selectedFlag : []
      allFilters.action = capacityFilters.selectedCapacitySubLvl === 'action' && capacityFilters.selectedAction.length ? capacityFilters.selectedAction : []
    }
    if (selectedCapacityAdvanceData.length === 0) return;
    setSelectedCapacityGeohashes([]);

    const advanceFilterArr = selectedCapacityAdvanceData.flatMap(
      ({ enodbid, sectors }) =>
        sectors.map((sector) => ({
          enodbID: enodbid,
          secID: sector.secID,
        }))
    );
    const filters = getCommonCapacityFilters(true,false)
    allFilters = {...allFilters, ...filters,advanceFilter: advanceFilterArr};
    let transformedArr = []
    try {
      await paginatedApiInvoker(
        (pageSize, page) =>
          getCapacityGeohashes(allFilters, false, null, pageSize, page),
        20000, // perPage
        null,
        (data) => {
          if (data) {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              capacity: {...allFilters,cancelToken: false},
            });

            data.flatMap((obj) => {
              if(obj?.flag && obj?.action){
                obj?.geohashes?.map((geohash) => {
                  const polygon = convertGeohashToPolygon(geohash.g);
                  transformedArr.push({
                    geohash: geohash.g,
                    action: obj.action,
                    flag: obj.flag,
                    month: obj.month,
                    secID: obj.secID,
                    enodbID: obj.enodbID,
                    operator: obj.operator,
                    nwCoordinate: polygon[0],
                    seCoordinate: polygon[3],
                  });
                });
              }
            });
            transformedArr = removeDuplicateGeoHashes(transformedArr)
            setCapacityGeohashes([]);
            setSelectedCapacityGeohashes(transformedArr);
          }
        }
      );
    } catch (err) {
      if (err?.response && err.response.data && err.response.data.errorMessage)
        toast.warn(err?.response?.data?.errorMessage, { theme: "colored" });
      console.log(err);
    }
  };

  // filters related
  const fetchFilterDataOnFocus = async (levelValue,initialFecth,page,searchVal) => {
    if (!capacityFilters.selectedOprtr || !capacityFilters.selectedMonth) return;
    if (levelValue === "secID" && !capacityFilters.selectedTower) return;
    
    let allFilters = {};
    if (searchVal) allFilters.searchVal = searchVal || "";
    allFilters.filterLvl = levelValue;
    const filters = getCommonCapacityFilters(false,false, true)
    allFilters = { ...allFilters, ...filters };

    const response = await getCapacityFilters(allFilters,false,null,10,page);
    const filterValues = response?.data?.length !== 0 ? response?.data?.map((filter) => filter._id) : [];

    if (levelValue === "enodbID") {
      if (initialFecth) {
        setCapacityFilterData((prev) => ({...prev,towers: filterValues,towerScroll: page}));
      } else {
        setCapacityFilterData((prev) => ({...prev,towerScroll: page}));
        return { items: filterValues, page: page, pageLimit: 10};
      }
    } else if (levelValue === "secID") {
      if (initialFecth) {
        setCapacityFilterData((prev) => ({...prev,sectors: filterValues,sectorScroll: page}));
      } else {
                setCapacityFilterData((prev) => ({...prev,sectorScroll: page}));
        return {items: capacityFilterData.sectors,page: page,pageLimit: 10};
      }
    }
  };

  const fetchInitialFilterData = async (levelValue) => {
    const allFilters = {
      countryCode: appliedFilters.countryCode || null,
      filterLvl: levelValue,
      ...(levelValue === "month" && { year: capacityFilters.selectedYear })
    };

    const response = await getCapacityFilters(allFilters, false, null, null, null);
    const formatResponse = response?.data?.length !== 0 ? response?.data?.map((filter) => filter._id).sort((a, b) => b - a) : [];
    if (levelValue === "year") {
      setCapacityFilterData((prev) => ({ ...prev, years: formatResponse }));
    } else if (levelValue === "month") {
      const sortOrder = formatResponse.length ? formatResponse.sort((a, b) => monthOrder[a] - monthOrder[b]) : [];
      setCapacityFilterData((prev) => ({ ...prev, months: sortOrder }));
    }
  };

  const loadAdvancedTableFilters = async () => {
    try {
      const { data } = await getCapacityAdvancedTableFilters({
        country: appliedFilters.countryCode, year: capacityFilters.selectedYear, month: capacityFilters.selectedMonth, op: capacityFilters.selectedOprtr
      });
      setAdvTableFilters({ flag: data?.flags, action: data?.actions, months: data?.months });
    } catch (e) {
      console.error(e)
    }
  }

  // year dropdown options
  const yearOptions = useMemo(() => {
    if (!capacityFilterData.years || capacityFilterData.years.length === 0) return [];
    const lowerYears = capacityFilterData.years.filter(
      (year) => year <= appliedFilters.year
    );

    if (!hasInitializedYear.current && lowerYears.length > 0) {
      // hasInitializedYear.current = true;
      onChangeYear(lowerYears[0]); // Set default year once
    }
    return lowerYears;
  }, [capacityFilterData.years, appliedFilters.year]);

// month dropdown options
  const monthOptions = useMemo(() => {
    if (
      !capacityFilters.selectedYear ||
      !capacityFilterData.months ||
      capacityFilterData.months.length === 0
    ) {
      return [];
    }
    const allowedMonths =
      monthsByQuarter[
        appliedFilters.year === capacityFilters.selectedYear
          ? appliedFilters.quarter
          : "Q4"
      ]; // Any month if capacity year is less than appliedFilter year
    const lowerMonths = capacityFilterData.months.filter((month) =>
      allowedMonths.includes(month)
    );

    if (!hasInitializedMonth.current && lowerMonths.length > 0) {
      onChangeMonth(lowerMonths[lowerMonths.length - 1]); // Set latest month once
      hasInitializedMonth.current = true;
    } else if (lowerMonths.length === 0) {
      onChangeMonth();
    }
    return lowerMonths;
  }, [
    capacityFilters.selectedYear,
    capacityFilterData.months,
    appliedFilters.year,
    appliedFilters.quarter,
  ]);
  return (
    <>
      {isPanelOpen
        ? <>
          <Grid container item style={{ padding: "5px 15px " }}>
            <Row>
              <Grid container spacing={0}>
                <Grid
                  container
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  justifyContent={"flex-start"}
                >
                  <FormControlStyled high>
                    <AdministrativeLevelLabel>Year</AdministrativeLevelLabel>
                    <TypeSelect
                      defaultVal={"Select Year"}
                      options={yearOptions}
                      control={capacityFilters.selectedYear}
                      setControl={onChangeYear}
                    />
                  </FormControlStyled>
                </Grid>
                <Grid
                  container
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  justifyContent={"flex-start"}
                  style={{ paddingLeft: "5px" }}
                >
                  <FormControlStyled high>
                    <AdministrativeLevelLabel>Month</AdministrativeLevelLabel>
                    <TypeSelect
                      defaultVal={"Select Month"}
                      options={monthOptions}
                      control={capacityFilters.selectedMonth}
                      setControl={onChangeMonth}

                    />
                  </FormControlStyled>
                </Grid>
              </Grid>
            </Row>
          </Grid>
          <Grid
            container
            item
            style={{
              textAlign: "left",
              font: "normal normal 550 13px/18px Open Sans",
              letterSpacing: "0px",
              color: "black",
              opacity: "1",
              padding: "10px",
            }}
          >
            <SitesDetailsTile
              title="Operators"
              selectedOperator={capacityFilters.selectedOprtr}
              onchangeOperator={onChangeCapacityOperator}
              permitedOperatorData={operatorData}
            />
          </Grid>
          <Grid container item style={{ padding: "5px 15px " }}>
            <Row>
              <AdministrativeLevelLabel>EnodeB ID</AdministrativeLevelLabel>
            </Row>
            <Row>
              <ScrollableTypeSelect
                disabled={selectedCapacityAdvanceData.length !== 0 ? true : false}
                dataOptions={capacityFilterData.towers}
                control={capacityFilters.selectedTower}
                setControl={onChangeTower}
                dataPage={capacityFilterData.towerScroll}
                loadData={fetchFilterDataOnFocus}
                prevLevelVal="enodbID"
                setDataOptions={(value) => {
                  setCapacityFilterData((prev) => ({ ...prev, towers: value }));
                }}
              />
              <ToolWrapper
                onClick={() => {
                  if (
                    capacityFilters.selectedMonth &&
                    capacityFilters.selectedOprtr
                  ) {
                    setCapacityAdvanceDisable(false);
                    // onDisableSubFilters();
                    onDisableTowerSector();
                    loadAdvanceCapacityData(true);
                  }
                }}
                isSelected={""}
                backgroundColor={
                  selectedCapacityAdvanceData.length !== 0
                    ? "#4DB84880"
                    : "#f0f0f0"
                }
                id="capacity-adv-icon"
              >
                <img
                  src={"/assets/searchIcon.png"}
                  width={"18px"}
                  height={"18px"}
                  alt="capacity advance search"
                  style={{
                    opacity:
                      capacityFilters.selectedMonth && capacityFilters.selectedOprtr
                        ? 1
                        : 0.5,
                  }}
                />
              </ToolWrapper>
              <DialogWrapper
                cancelLabel="Cancel"
                title="Capacity"
                actionLabel={"Confirm Selection"}
                open={openAdvanceCapacityData}
                version
                vw="90vw" // width
                isMaxWidthEnable={false}
                isSiteRefModal={true}
                setOpenModal={setOpenAdvanceCapacityData}
                acceptButton={() => {
                  if (selectedCapacityAdvanceData.length !== 0) {
                    updateAdvanceSelectedData();
                    setOpenAdvanceCapacityData(false);
                    resetFiltersInBaseSearch();
                    loadAdvanceSelectedGeohashes();
                    onChangeCapacitySubFilters(true);
                  } else {
                    updateAdvanceSelectedData();
                    onChangeCapacitySubFilters()
                    setCapacityAdvanceDisable(true)
                    setOpenAdvanceCapacityData(false);
                  }
                }}
                cancelAction={() => {
                  setOpenAdvanceCapacityData(false);
                  resetAdvanceSelectedData()
                }}
                onClose={() => {
                  setOpenAdvanceCapacityData(false);
                  resetAdvanceSelectedData()
                }}
              >
                <CapacityAdvanceFilter // Table
                  headCells={AdvanceCapacityHeaders}
                  dataRows={advanceCapacityData}
                  loadData={loadAdvanceCapacityData}
                  getSelectedData={getSelectedCapacityData}
                  selectedFilters={appliedFilters}
                  selectedMonth={capacityFilters.selectedMonth}
                  selectedOperator={capacityFilters.selectedOprtr}
                  selectedYear={capacityFilters.selectedYear}
                  dynamicFilters={advTableFilters} // flags, actions, months values
                />
              </DialogWrapper>
            </Row>
            <Row>
              <AdministrativeLevelLabel>Sector ID</AdministrativeLevelLabel>
            </Row>
            <Row>
              <ScrollableTypeSelect
                disabled={selectedCapacityAdvanceData.length !== 0 ? true : false}
                dataOptions={capacityFilterData.sectors}
                control={capacityFilters.selectedSector}
                setControl={onChangeSector}
                dataPage={capacityFilterData.sectorScroll}
                loadData={fetchFilterDataOnFocus}
                prevLevelVal="secID"
                setDataOptions={(value) => {
                  setCapacityFilterData((prev) => ({ ...prev, sectors: value }));
                }}
              />
            </Row>
          </Grid>
          {capacityFilters.selectedCapacitySubLvl ? <RadioGroup
            defaultValue={capacityFilters.selectedCapacitySubLvl}
            value={capacityFilters.selectedCapacitySubLvl}
            onChange={(e) => {
              setCapacityFilters({
                ...capacityFilters,
                selectedCapacitySubLvl: e.target.value
              })
            }}
          >
            {Object.entries(capacitySubFilters.flags).length !== 0 ? (
              <Grid
                container
                item
                style={{
                  textAlign: "left",
                  font: "normal normal 550 13px/18px Open Sans",
                  letterSpacing: "0px",
                  color: "black",
                  opacity: "1",
                  padding: "0px 10px 10px",
                }}
              >
                <Grid
                  container
                  xs={12}
                  sm={12}
                  md={12}
                  style={{
                    justifyContent: "space-between",
                    margin: "5px 5px 0px",
                    padding: "10px",
                    background: "#FFFFFF 0% 0% no-repeat padding-box",
                    border: "1px solid #007842",
                    borderRadius: "5px",
                    opacity: "1",
                  }}
                >
                  <FormControlLabel
                    value="flag"
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 16,
                          },
                        }}
                      />
                    }
                    label={
                      <Box component="div" style={{
                        textAlign: "left",
                        font: "normal normal 550 13px/18px Open Sans",
                        letterSpacing: "0px",
                        color: "black",
                        opacity: "1",

                      }}>
                        Flag
                      </Box>
                    }
                  />

                  {Object.keys(capacitySubFilters.flags).map((key) => (
                    <Grid
                      container
                      xs={12}
                      sm={12}
                      md={12}
                      style={{
                        justifyContent: "space-between",
                        paddingTop: "5px",
                      }}
                      key={key}
                    >
                      <ToggleTitleWrapper>{key}</ToggleTitleWrapper>
                      <ToggleSwitch
                        disabled={capacitySubFilters.flags[key].editable}
                        checked={capacitySubFilters.flags[key].check}
                        onChange={(e) => {
                          onChangeFlag(e, key);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
            {Object.entries(capacitySubFilters.actions).length !== 0 ? (
              <Grid
                container
                item
                style={{
                  textAlign: "left",
                  font: "normal normal 550 13px/18px Open Sans",
                  letterSpacing: "0px",
                  color: "black",
                  opacity: "1",
                  padding: "0px 10px 10px",
                }}
              >
                <Grid
                  container
                  xs={12}
                  sm={12}
                  md={12}
                  style={{
                    justifyContent: "space-between",
                    margin: "5px 5px 0px",
                    padding: "10px",
                    background: "#FFFFFF 0% 0% no-repeat padding-box",
                    border: "1px solid #007842",
                    borderRadius: "5px",
                    opacity: "1",
                  }}
                >
                  <FormControlLabel
                    value="action"
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 16,
                          },
                        }}
                      />
                    }
                    label={
                      <Box component="div" style={{
                        textAlign: "left",
                        font: "normal normal 550 13px/18px Open Sans",
                        letterSpacing: "0px",
                        color: "black",
                        opacity: "1",

                      }}>
                        Action
                      </Box>
                    }
                  />
                  {Object.keys(capacitySubFilters.actions).map((key) => (
                    <Grid
                      container
                      xs={12}
                      sm={12}
                      md={12}
                      style={{
                        justifyContent: "space-between",
                        paddingTop: "5px",
                      }}
                      key={key}
                    >
                      <ToggleTitleWrapper>{key}</ToggleTitleWrapper>
                      <ToggleSwitch
                        disabled={capacitySubFilters.actions[key].editable}
                        checked={capacitySubFilters.actions[key].check}
                        onChange={(e) => {
                          onChangeAction(e, key);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
          </RadioGroup> : <></>}
        </>
        : <></>}
    </>
  );
}
